/**
 * ProfileTabs - Horizontal scrollable tabs for profile sections
 * Based on mobile app ProfileScreenOptimized.tsx
 */

import { useState } from 'react'
import { Grid3x3, MessageSquare, Heart, Bookmark, Lock, Share2 } from 'lucide-react'

export type TabType = 'posts' | 'media' | 'replies' | 'likes' | 'saved' | 'private' | 'shared'

interface ProfileTabsProps {
  activeTab: TabType
  onTabChange: (tab: TabType) => void
  isOwnProfile?: boolean // Show all tabs only if viewing own profile
}

interface Tab {
  id: TabType
  label: string
  icon: React.ReactNode
  requiresOwner?: boolean // Only show for profile owner
}

export default function ProfileTabs({ activeTab, onTabChange, isOwnProfile = false }: ProfileTabsProps) {
  const tabs: Tab[] = [
    { id: 'posts', label: 'Posts', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'media', label: 'Media', icon: <Grid3x3 className="w-4 h-4" /> },
    { id: 'replies', label: 'Replies', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'likes', label: 'Likes', icon: <Heart className="w-4 h-4" /> },
    { id: 'saved', label: 'Saved', icon: <Bookmark className="w-4 h-4" />, requiresOwner: true },
    { id: 'private', label: 'Private', icon: <Lock className="w-4 h-4" />, requiresOwner: true },
    { id: 'shared', label: 'Shared', icon: <Share2 className="w-4 h-4" />, requiresOwner: true },
  ]

  // Filter tabs based on profile ownership
  const visibleTabs = tabs.filter(tab => !tab.requiresOwner || isOwnProfile)

  return (
    <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
      {/* Horizontal Scrollable Tabs */}
      <div className="flex overflow-x-auto scrollbar-hide">
        {visibleTabs.map(tab => {
          const isActive = activeTab === tab.id

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 min-w-fit whitespace-nowrap
                transition-colors relative
                ${isActive
                  ? 'text-onrecord-orange font-semibold'
                  : 'text-text-secondary hover:text-text-primary'
                }
              `}
            >
              {tab.icon}
              <span className="text-post">{tab.label}</span>

              {/* Active Indicator */}
              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-onrecord-orange rounded-t-full" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
